import { Stepper } from '@axo/deprecated/util/ui-components';
import { MyPageFlags } from '@axo/mypage/util';
import { insurance } from '@axo/shared/data-access/types';
import { useFlags } from '@axo/shared/services/feature-flags';
import { ITrustpilot, Trustpilot } from '@axo/shared/ui/core';
import '@axo/shared/ui/core/styles/mixins.scss';
import clsx from 'clsx';
import { Dispatch, useMemo } from 'react';
import { CurrentStep } from './CurrentStep';
import { useSetInsuranceStepOnSearchParams } from './hooks/useSetInsuranceStepOnSearchParams';
import styles from './StepProvider.module.scss';
import { StepperAction } from './store/actions';
import { OnAccept, OnEnd, StepContext } from './store/context';
import { StepperState } from './store/reducer';
import { StepList, Steps } from './store/stepStates';

export type AppSpecificStepProviderProps = {
  onAccept?: OnAccept;
  onEnd: OnEnd;
  onError: (error: Error) => void;
  onClose?: () => void;
  isEmbedded?: boolean;
  sourceTag: insurance.InsuranceSourceTag;
  classes?: {
    stepper?: string;
  };
  initialStep?: keyof Steps;
};

export type StepProviderProps = AppSpecificStepProviderProps & {
  steps: StepList;
  state: StepperState;
  dispatch: Dispatch<StepperAction>;
  locale: 'fi' | 'no';
  trustpilotConfig: Pick<ITrustpilot, 'businessId' | 'locale' | 'link'>;
};

export function StepProvider({
  steps,
  onAccept,
  onEnd,
  onError = () => undefined,
  onClose,
  isEmbedded = false,
  sourceTag,
  state,
  dispatch,
  locale,
  trustpilotConfig,
  classes,
  initialStep,
}: StepProviderProps) {
  const {
    'offer-insurance-on-accept': flag_offer_insurance_on_accept,
    'accept-insurance-widget': flag_accept_insurance_widget,
  } = useFlags(MyPageFlags);

  useSetInsuranceStepOnSearchParams(state.selectedStep);

  const showStepperInCurrentStep =
    state.steps?.[state.selectedStep]?.isShownInStepper;

  const stepContextValue = useMemo(
    () => ({
      state,
      dispatch,
      onAccept,
      onEnd,
      onError,
      onClose,
      isEmbedded,
      sourceTag,
      initialStep,
    }),
    [
      state,
      dispatch,
      onAccept,
      onEnd,
      onError,
      onClose,
      isEmbedded,
      sourceTag,
      initialStep,
    ]
  );

  return (
    <StepContext.Provider value={stepContextValue}>
      {!isEmbedded && showStepperInCurrentStep && (
        <div className={styles.stepper}>
          <Stepper
            locale={locale}
            steps={Object.values(state.steps).filter(
              (step) => step.isShownInStepper
            )}
            hideHeadline
          />
        </div>
      )}
      <div className={isEmbedded ? styles.contentFull : styles.contentSplit}>
        <div
          className={clsx(
            styles.mainColumn,
            isEmbedded && styles.background,
            (flag_offer_insurance_on_accept || flag_accept_insurance_widget) &&
              classes?.stepper
          )}
        >
          {state.error !== undefined && steps.error !== undefined ? (
            steps.error
          ) : (
            <CurrentStep
              name={state.selectedStep}
              component={steps[state.selectedStep]}
            />
          )}
        </div>
        {!isEmbedded && (
          <div className={styles.trustpilot}>
            <Trustpilot
              templateId="53aa8807dec7e10d38f59f32"
              height="150px"
              {...trustpilotConfig}
            />
          </div>
        )}
      </div>
    </StepContext.Provider>
  );
}
